import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createLead } from '../api/leadService';
import { buildCreateLeadPayload, LEAD_STATUSES, LEAD_PRIORITIES } from '../utils/leadModel';

const LEAD_SOURCES = [
  'Website',
  'Referral',
  'Cold Call',
  'Email Campaign',
  'Social Media',
  'LinkedIn',
  'Trade Show / Event',
  'Partner',
  'Advertisement',
  'Organic Search',
  'Paid Search',
  'Content / Blog',
  'Webinar',
  'Other',
];

function LeadFormPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    lead_source: '',
    priority: 'Medium',
    status: 'New',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
    setServerError('');
  };

  const validate = () => {
    const next = {};
    if (!form.first_name.trim()) next.first_name = 'First name is required.';
    if (!form.last_name.trim()) next.last_name = 'Last name is required.';
    if (!form.email.trim()) {
      next.email = 'Email is required.';
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      next.email = 'Enter a valid email address.';
    }
    if (!form.company_name.trim()) next.company_name = 'Company name is required.';
    if (!form.lead_source.trim()) next.lead_source = 'Lead source is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError('');
    try {
      await createLead(buildCreateLeadPayload(form));
      navigate('/leads');
    } catch (err) {
      const serverErrs = err?.response?.data?.errors;
      if (Array.isArray(serverErrs) && serverErrs.length) {
        const fieldErrors = {};
        serverErrs.forEach((e) => { if (e.field) fieldErrors[e.field] = e.message; });
        setErrors(fieldErrors);
      } else {
        setServerError(err?.response?.data?.message || 'Failed to create lead.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="page-title-row">
        <h2>Create Lead</h2>
      </div>
      <form className="compact-form" onSubmit={onSubmit} noValidate>

        <div className="form-row">
          <label>
            <span>First Name <span className="required-mark">*</span></span>
            <input name="first_name" value={form.first_name} onChange={onChange} autoComplete="given-name" />
            {errors.first_name && <span className="error-text">{errors.first_name}</span>}
          </label>
          <label>
            <span>Last Name <span className="required-mark">*</span></span>
            <input name="last_name" value={form.last_name} onChange={onChange} autoComplete="family-name" />
            {errors.last_name && <span className="error-text">{errors.last_name}</span>}
          </label>
        </div>

        <div className="form-row">
          <label>
            <span>Email <span className="required-mark">*</span></span>
            <input name="email" type="email" value={form.email} onChange={onChange} autoComplete="email" />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </label>
          <label>
            <span>Phone</span>
            <input name="phone" type="tel" value={form.phone} onChange={onChange} autoComplete="tel" />
          </label>
        </div>

        <div className="form-row">
          <label>
            <span>Company Name <span className="required-mark">*</span></span>
            <input name="company_name" value={form.company_name} onChange={onChange} />
            {errors.company_name && <span className="error-text">{errors.company_name}</span>}
          </label>
          <label>
            <span>Lead Source <span className="required-mark">*</span></span>
            <select name="lead_source" value={form.lead_source} onChange={onChange}>
              <option value="">— Select a source —</option>
              {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.lead_source && <span className="error-text">{errors.lead_source}</span>}
          </label>
        </div>

        <div className="form-row">
          <label>
            <span>Priority</span>
            <select name="priority" value={form.priority} onChange={onChange}>
              {LEAD_PRIORITIES.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select name="status" value={form.status} onChange={onChange}>
              {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>

        <label>
          <span>Notes</span>
          <textarea name="notes" value={form.notes} onChange={onChange} rows={2} />
        </label>

        {serverError && <p className="error-text">{serverError}</p>}

        <div className="form-actions">
          <button type="submit" className="primary-btn" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create Lead'}
          </button>
          <button type="button" className="ghost-btn" onClick={() => navigate('/leads')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default LeadFormPage;
